<template>
  <div>
    <ProgressBar active-step="Tell us about your teen" />
    <div>
      <PageHeading
        :default-heading="pageHeader"
        :edit-heading="pageHeader"
      />
      <form
        novalidate="novalidate"
        @submit.prevent
      >
        <div class="pvd-container container-divider">
          <h2 class="pvd-heading-L">
            {{ pd_content.joint_personal_information_section_header }}
          </h2>
          <apx-full-name
            id="fullName"
            :group-label="pd_content.name_label"
            :first-name="su_firstName"
            :first-name-label="pd_content.first_name_label"
            :middle-name="su_middleName"
            :middle-name-label="pd_content.middle_name_label"
            :last-name="su_lastName"
            :last-name-label="pd_content.last_name_label"
            required
          />
          <apx-ssn
            id="ssn"
            :value="su_ssn"
            :label="pd_content.ssn_label"
            required
          />
          <div class="pvd-form-row top-margin-zero">
            <div class="form-row-label">
              &nbsp;
            </div>
            <div class="form-row-content">
              &nbsp;
            </div>
            <InlineMessage
              v-if="ssnMatchesExistingSSN"
              :message="pd_content.ssn_validation_error"
              css-class="form-row-message p-small-top"
            />
          </div>
          <apx-date-select
            id="dateOfBirth"
            :group-label="pd_content.dob_label"
            :month-value="month"
            :month-label="pd_content.dob_month_label"
            :day-value="date"
            :day-label="pd_content.dob_day_label"
            :year-value="year"
            :year-label="pd_content.dob_year_label"
            default-select-label="Select"
            :min-date="minDateDobValidation"
            :max-date="maxDateDobValidation"
            :min-date-error="minDateError"
            :max-date-error="maxDateError"
            required
            :date-input-value="su_dob"
          />
        </div>
        <PageNavigation
          show-primary-button-spinner="N"
          enable-exit-modal="Y"
          :show-secondary-button="showPreviousBtn"
          show-page-spinner="N"
          primary-button-type="button"
          :primary-button-label="getPrimaryButtonLabel"
          :secondary-button-label="getSecondaryButtonLabel"
          :page-exit-url="computedExitUrl"
          @primary-button-clicked="nextButtonClicked"
        />
        <DisclosureComponent
          :additional-disclosure="pd_content.page_disclosure_additional"
          :paragraph-disclosure="pd_content.page_disclosure_paragraph"
        />
      </form>
    </div>
  </div>
</template>

<script>
import PageNavigation from '../components/PageNavigation.vue';
import PageHeading from '../components/PageHeading.vue';
import utility from '../mixins/utility';
import dataHelper from '../mixins/dataHelper';
import ProgressBar from '../components/ProgressBar.vue';
import pageNavigationHelper from '../mixins/pageNavigationHelper';
import dataSanitize from '../mixins/dataSanitize';
import DisclosureComponent from '../components/DisclosureComponent.vue';
import constants from '../mixins/constants';
import InlineMessage from '../components/InlineMessage.vue';

export default {
  name: 'JointPersonalInformation',
  components: {
    PageHeading,
    ProgressBar,
    PageNavigation,
    DisclosureComponent,
    InlineMessage,
  },
  mixins: [dataHelper, pageNavigationHelper, utility, dataSanitize],
  computed: {
    pageHeader() {
      const pageHeader = this.pd_content.page_header;
      if (pageHeader) {
        const heading = this.cs_editMode ? pageHeader.edit : pageHeader.default;

        if (heading) {
          document.title = this.setTitle(heading);
        }
        return heading;
      }
      return '';
    },
    month() {
      if (this.su_dob) {
        const splitDob = this.su_dob.split('/');
        return splitDob[0];
      }
      return '';
    },
    date() {
      if (this.su_dob) {
        const splitDob = this.su_dob.split('/');
        return splitDob[1];
      }
      return '';
    },
    year() {
      if (this.su_dob) {
        const splitDob = this.su_dob.split('/');
        return splitDob[2];
      }
      return '';
    },
    isManagedAccount() {
      return this.isManaged(this.routeId);
    },
    minDateDobValidation() {
      let minDate = `${this.pd_content.joint_dob_validation.minDate}`;
      if (!this.isManagedAccount) {
        minDate = this.subtractNow(18, 'min');
      }
      return minDate;
    },
    maxDateDobValidation() {
      let maxDate = this.subtractNow(18);
      if (!this.isManagedAccount) {
        maxDate = this.subtractNow(13);
      }
      return maxDate;
    },
    minDateError() {
      let minDateError = this.pd_content.joint_dob_validation.minErr;
      if (!this.isManagedAccount) {
        minDateError = this.pd_content.youth_date_validation.minError;
      }
      return minDateError;
    },
    maxDateError() {
      let maxDateError = this.pd_content.joint_dob_validation.maxErr;
      if (!this.isManagedAccount) {
        maxDateError = this.pd_content.youth_date_validation.maxError;
      }
      return maxDateError;
    },
    showPreviousBtn() {
      if ((this.pu_flowType === constants.FLOW_TYPES.EXPRESS && this.cs_editMode)
      || (this.isManagedAccount && this.pu_flowType !== constants.FLOW_TYPES.EXPRESS)) {
        return 'Y';
      }
      return this.pu_flowType === constants.FLOW_TYPES.LONG ? 'Y' : 'N';
    },
    ssnMatchesExistingSSN: {
      get() {
        return this.pu_ssn === this.su_ssn;
      },
    },
  },
  created() {
    if (this.cs_editMode) {
      this.unsubscribe = this.sanitizeInstanceData('secondaryUser');
    }
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  mounted() {
    this.trackEdit(this.cs_editMode, 'edit-joint-personal-information');
    const changeEventName = 'value-changed';
    this.configureBackButtonFunctionality(() => this.showBackWarning(
      this.pd_content.back_button_disabled_warning,
    ));

    const fullNameComponent = document.querySelector('#fullName');
    fullNameComponent.addEventListener(changeEventName, this.fullNameChanged);
    fullNameComponent.validationRules = this.buildRules('fullName');

    const ssnComponent = document.querySelector('#ssn');
    ssnComponent.addEventListener(changeEventName, this.ssnChanged);
    ssnComponent.validationRules = this.buildRules('ssn');

    const dobComponent = document.querySelector('#dateOfBirth');
    dobComponent.addEventListener(changeEventName, this.dateOfBirthChanged);
    dobComponent.validationRules.date = this.pd_validationRules.customerIdentity.birthDate;
  },
  methods: {
    setNextButtonClicked() {
      this.configureBackButtonFunctionality(() => this.showBackWarning(
        this.pd_content.back_button_disabled_warning,
      ));
    },
    nextButtonClicked(navAction) {
      this.performPageValidation(navAction).then((valid) => {
        if (valid && !this.ssnMatchesExistingSSN) {
          this.performNavigation(this.pd_content.primary_button.value);
        }
      });
    },
    fullNameChanged(event) {
      this.commitSecondaryUserData(event.detail.fullname.firstName, 'firstName');
      this.commitSecondaryUserData(event.detail.fullname.middleName, 'middleName');
      this.commitSecondaryUserData(event.detail.fullname.lastName, 'lastName');
    },
    ssnChanged(event) {
      this.commitSecondaryUserData(event.detail.value, 'ssn');
    },
    dateOfBirthChanged(event) {
      this.commitSecondaryUserData(`${event.detail.month}/${event.detail.day}/${event.detail.year}`, 'dob');
    },
  },
};
</script>
